import React, { Component } from "react";
import { Col } from "react-grid-system";
import { IoClose } from "react-icons/io5";
import { LiaBroomSolid } from "react-icons/lia";
import { connect } from "react-redux";
import { Dropdown, Pagination } from "semantic-ui-react";
import { ProductModel } from "../../entities/admin.product.model";
import ProductItem from "../product-item/product-item";
import ProductsFilter from "./filter";
import "./products.scss";

interface ProductsProps {
  products: ProductModel[];
  client?: any;
  locationSearch?: string;
  /** Load more results on scroll instead of pagination controls */
  infiniteScroll?: boolean;
}

interface ProductsState {
  filteredProducts: ProductModel[];
  filtersOpen: boolean;
  sale: boolean;
  notSale: boolean;
  club: boolean;
  searchValue: string;
  // parameters
  brandSet: string[];
  usageSet: string[];
  typeSet: string[];
  sortOptions: any[];
  sortValue: string;
  currentPage: number;
  pageSize: number | string;
  visibleCount: number;
  getTypeCount?: (type: string) => number;
  getUsageCount?: (usage: string) => number;
  getBrandCount?: (brand: string) => number;
  getSaleCount?: (sale: string) => number;
  getNotSaleCount?: (notSale: string) => number;
  getClubCount?: () => number;
}

class Products extends Component<ProductsProps, ProductsState> {
  unitValue: any = { unit: "", min: 0, max: 0 };
  filteredBrands: Set<String> = new Set();
  filteredUsages: Set<String> = new Set();
  filteredTypes: Set<String> = new Set();
  loadMoreSentinelRef = React.createRef<HTMLDivElement>();
  infiniteScrollObserver: IntersectionObserver | null = null;
  pageSizeOptions = [
    { key: 50, value: 50, text: '50' },
    { key: 100, value: 100, text: '100' },
    { key: 200, value: 200, text: '200' },
    { key: 'all', value: 'all', text: 'Бүгд' }
  ];

  constructor(props: any) {
    super(props);
    this.state = {
      filteredProducts: [],
      sale: false,
      notSale: false,
      club: false,
      searchValue: "",
      filtersOpen: false,
      brandSet: [],
      usageSet: [],
      typeSet: [],
      sortOptions: [
        { key: "lastfirst", value: "lastfirst", text: "Шинэ нь эхэндээ" },
        { key: "priceup", value: "priceup", text: "Үнэ - багаас их" },
        { key: "pricedown", value: "pricedown", text: "Үнэ - ихээс бага" },
        { key: "saleup", value: "saleup", text: "Хямдарсан" },
      ],
      sortValue: "lastfirst",
      currentPage: 1,
      pageSize: 50,
      visibleCount: 50,
    };

    this.onSortChange = this.onSortChange.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
    this.onPageSizeChange = this.onPageSizeChange.bind(this);
    this.loadMore = this.loadMore.bind(this);
  }

  getScrollPageSize(): number {
    const { pageSize } = this.state;
    return typeof pageSize === 'number' ? pageSize : 50;
  }

  getInitialVisibleCount(): number {
    return this.getScrollPageSize();
  }

  componentDidMount() {
    let sale = this.isSaleFilterActive();
    let searchValue = this.hasSearchValue() || "";
    let products = this.props.products;
    let querySortValue = this.isSortQueryActive();
    this.setState(
      {
        filteredProducts: products,
        sale: sale,
        searchValue: searchValue,
        sortValue: querySortValue || "lastfirst",
      },
      () => {
        this.applyFilter();
      }
    );
    this.prepareFilters(this.props);
    if (this.props.infiniteScroll) {
      this.setupInfiniteScrollObserver();
    }
  }

  componentWillUnmount() {
    this.teardownInfiniteScrollObserver();
  }

  teardownInfiniteScrollObserver() {
    if (this.infiniteScrollObserver) {
      this.infiniteScrollObserver.disconnect();
      this.infiniteScrollObserver = null;
    }
  }

  setupInfiniteScrollObserver() {
    if (!this.props.infiniteScroll) {
      return;
    }
    this.teardownInfiniteScrollObserver();
    const node = this.loadMoreSentinelRef.current;
    if (!node) {
      return;
    }
    this.infiniteScrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          this.loadMore();
        }
      },
      { root: null, rootMargin: '240px', threshold: 0 }
    );
    this.infiniteScrollObserver.observe(node);
  }

  hasMoreToLoad(): boolean {
    const { filteredProducts, visibleCount } = this.state;
    return (filteredProducts?.length || 0) > visibleCount;
  }

  loadMore() {
    if (!this.props.infiniteScroll || !this.hasMoreToLoad()) {
      return;
    }
    const step = this.getScrollPageSize();
    this.setState((prev) => ({
      visibleCount: Math.min(prev.visibleCount + step, prev.filteredProducts.length),
    }));
  }

  componentWillReceiveProps(nextProps: any) {
    if (nextProps.products) {
      // Check if products actually changed by comparing IDs
      const currentProductIds = this.props.products?.map((p: any) => p.id).join(',') || '';
      const nextProductIds = nextProps.products?.map((p: any) => p.id).join(',') || '';
      const productsChanged = currentProductIds !== nextProductIds;
      
      if (productsChanged) {
        let products: ProductModel[] = [];
        for (let p of nextProps.products) {
          if (!p.active) continue;
          products.push(p);
        }
        let sale = this.isSaleFilterActive();
        let searchValue = this.hasSearchValue() || "";
        // Create a copy before sorting to avoid mutating
        const productsCopy = [...products];
        productsCopy.sort((a: any, b: any) => {
          if (
            (a.warehouseCount <= 0 && b.warehouseCount <= 0) ||
            (a.warehouseCount > 0 && b.warehouseCount > 0)
          ) {
            // id desc
            return a.id < b.id ? 1 : -1;
          } else {
            // stocked first
            return a.warehouseCount <= 0 ? 1 : -1;
          }
        });
        this.setState(
          { filteredProducts: productsCopy, sale: sale, searchValue: searchValue },
          () => {
            this.applyFilter();
          }
        );
      }
    }
    this.prepareFilters(nextProps);
  }

  prepareFilters(props: any) {
    if (props.products && props.products.length > 0) {
      let brandSet = new Set<string>();
      let usageSet = new Set<string>();
      let typeSet = new Set<string>();

      for (let product of props.products) {
        // brands
        if (!brandSet.has(product.brandName)) {
          brandSet.add(product.brandName);
        }
        // usages
        for (let usage of product.usages) {
          if (!usageSet.has(usage)) {
            usageSet.add(usage);
          }
        }
        // types
        for (let ptype of product.types) {
          if (!typeSet.has(ptype)) {
            typeSet.add(ptype);
          }
        }
      }
      // this.priceValue = { min: priceMin, max: priceMax };
      this.setState({
        brandSet: Array.from(brandSet.values()),
        usageSet: Array.from(usageSet.values()),
        typeSet: Array.from(typeSet.values()),
      });
    }
  }

  onFilterChange(e?: any) {
    if (e) {
      if (e.sale !== undefined) {
        this.setState({ sale: e.sale }, () => this.applyFilter());
      }
      if (e.notSale !== undefined) {
        this.setState({ notSale: e.notSale }, () => this.applyFilter());
      }
      if (e.club !== undefined) {
        this.setState({ club: e.club }, () => this.applyFilter());
      }
      if (e.brands !== undefined) {
        this.filteredBrands = e.brands;
        this.applyFilter();
      }
      if (e.usages !== undefined) {
        this.filteredUsages = e.usages;
        this.applyFilter();
      }
      if (e.types !== undefined) {
        this.filteredTypes = e.types;
        this.applyFilter();
      }
    }
  }

  applyFilter() {
    let filteredProducts = this.props.products.filter((p: any) => {
      // sale
      if (this.state.sale && !p.saleActive) return false;
      // name
      if (this.state.searchValue) {
        if (
          String(p.name)
            .toLowerCase()
            .indexOf(this.state.searchValue.toLowerCase()) === -1
        ) {
          return false;
        }
      }
      //notsale
      if (this.state.notSale && p.saleActive) return false;
      // club price (only relevant for club members)
      if (
        this.props.client?.company?.clubMember &&
        this.state.club &&
        !(p.clubPrice != null && Number(p.clubPrice) > 0)
      ) {
        return false;
      }
      // brand
      if (
        this.filteredBrands &&
        this.filteredBrands.size > 0 &&
        !this.filteredBrands.has(p.brandName)
      ) {
        return false;
      }
      // usage
      if (this.filteredUsages && this.filteredUsages.size > 0) {
        if (!p.usages) return false;
        let usageMatches = false;
        this.filteredUsages.forEach((fusage) => {
          for (let usage of p.usages) {
            if (fusage === usage) {
              usageMatches = true;
              break;
            }
          }
        });
        if (!usageMatches) return false;
      }
      // type
      if (this.filteredTypes && this.filteredTypes.size > 0) {
        if (!p.types) return false;
        let typeMatches = false;
        this.filteredTypes.forEach((ftype) => {
          for (let type of p.types) {
            if (ftype === type) {
              typeMatches = true;
              break;
            }
          }
        });
        if (!typeMatches) return false;
      }
      return true;
    });
    // Create a copy before sorting to avoid mutating state
    const sortedProducts = [...filteredProducts];
    this.sortProducts(sortedProducts, this.state.sortValue);
    
    // Only update state if the product IDs or their order have actually changed
    // This prevents unnecessary re-renders when basket changes but products/content are the same
    const currentIds = this.state.filteredProducts?.map((p: any) => p.id).join(',') || '';
    const newIds = sortedProducts.map((p: any) => p.id).join(',');
    
    // Update if products changed, order changed, or if this is the first filter (no current state)
    if (currentIds === '' || currentIds !== newIds) {
      this.setState({
        filteredProducts: sortedProducts,
        currentPage: 1,
        visibleCount: this.getInitialVisibleCount(),
      });
    }
  }

  isSaleFilterActive() {
    if (window?.location?.search) {
      let str = String(window?.location?.search);
      let arr: string[] = str.split("?");
      if (arr.length === 0) return false;
      let saleStr = arr[1].toLowerCase();
      if (saleStr && saleStr.indexOf("sale=") !== -1) {
        arr = saleStr.split("sale=");
        saleStr = arr[1];
        if (saleStr) {
          arr = saleStr.split("&");
          saleStr = arr[0];
          if (saleStr) {
            // found sale query param
            if (saleStr === "1") {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  isSortQueryActive() {
    if (window?.location?.search) {
      let str = String(window?.location?.search);
      let arr: string[] = str.split("?");
      if (arr.length === 0) return;
      let queryParam = arr[1].toLowerCase();
      if (queryParam && queryParam.indexOf("sort=") !== -1) {
        arr = queryParam.split("sort=");
        queryParam = arr[1];
        if (queryParam) {
          arr = queryParam.split("&");
          queryParam = arr[0];
          if (queryParam) {
            return queryParam;
          }
        }
      }
    }
  }

  hasSearchValue() {
    const search = this.props.locationSearch ?? window?.location?.search ?? "";
    if (!search) return;

    const params = new URLSearchParams(search);
    return params.get("value") || undefined;
  }

  onSortChange(e: any, drop: any) {
    if (drop.value !== this.state.sortValue) {
      if (this.state.filteredProducts?.length > 0) {
        // Create a copy of the array to avoid mutating state directly
        const sortedProducts = [...this.state.filteredProducts];
        this.sortProducts(sortedProducts, drop.value);
        this.setState({ 
          sortValue: drop.value, 
          currentPage: 1,
          visibleCount: this.getInitialVisibleCount(),
          filteredProducts: sortedProducts
        });
      } else {
        this.setState({
          sortValue: drop.value,
          currentPage: 1,
          visibleCount: this.getInitialVisibleCount(),
        });
      }
    }
  }

  onPageChange(event: any, data: any) {
    this.setState({ currentPage: data.activePage }, () => {
      window.scrollTo(0, 0);
    });
  }

  onPageSizeChange(event: any, data: any) {
    const pageSize = data.value;
    const nextState: Pick<ProductsState, 'pageSize' | 'currentPage' | 'visibleCount'> = {
      pageSize,
      currentPage: 1,
      visibleCount: this.state.visibleCount,
    };
    if (this.props.infiniteScroll && typeof pageSize === 'number') {
      nextState.visibleCount = pageSize;
    } else if (pageSize === 'all') {
      nextState.visibleCount = Number.MAX_SAFE_INTEGER;
    }
    this.setState(nextState);
  }

  getPaginatedProducts() {
    const { filteredProducts } = this.state;
    const { currentPage, pageSize, visibleCount } = this.state;
    
    if (!filteredProducts || filteredProducts.length === 0) {
      return [];
    }

    if (this.props.infiniteScroll) {
      return filteredProducts.slice(0, visibleCount);
    }

    if (pageSize === 'all') {
      return filteredProducts;
    }

    const startIndex = (currentPage - 1) * (pageSize as number);
    const endIndex = startIndex + (pageSize as number);
    return filteredProducts.slice(startIndex, endIndex);
  }

  getTotalPages() {
    const { filteredProducts } = this.state;
    const { pageSize } = this.state;
    
    if (!filteredProducts || filteredProducts.length === 0) {
      return 1;
    }

    if (pageSize === 'all') {
      return 1;
    }

    return Math.ceil(filteredProducts.length / (pageSize as number));
  }

  sortProducts(products: any[], sortValue: string) {
    if (sortValue === "lastfirst") {
      products.sort((a: any, b: any) => {
        if (
          (a.warehouseCount <= 0 && b.warehouseCount <= 0) ||
          (a.warehouseCount > 0 && b.warehouseCount > 0)
        ) {
          // id desc
          return a.id < b.id ? 1 : -1;
        } else {
          // stocked first
          return a.warehouseCount <= 0 ? 1 : -1;
        }
      });
    } else if (sortValue === "priceup") {
      products.sort((a: any, b: any) => {
        if (
          (a.warehouseCount <= 0 && b.warehouseCount <= 0) ||
          (a.warehouseCount > 0 && b.warehouseCount > 0)
        ) {
          let priceA = a.saleActive ? a.salePrice : a.price;
          let priceB = b.saleActive ? b.salePrice : b.price;
          // price asc
          return priceA < priceB ? -1 : 1;
        } else {
          // stocked first
          return a.warehouseCount <= 0 ? 1 : -1;
        }
      });
    } else if (sortValue === "pricedown") {
      products.sort((a: any, b: any) => {
        if (
          (a.warehouseCount <= 0 && b.warehouseCount <= 0) ||
          (a.warehouseCount > 0 && b.warehouseCount > 0)
        ) {
          let priceA = a.saleActive ? a.salePrice : a.price;
          let priceB = b.saleActive ? b.salePrice : b.price;
          // price desc
          return priceA < priceB ? 1 : -1;
        } else {
          // stocked first
          return a.warehouseCount <= 0 ? 1 : -1;
        }
      });
    } else if (sortValue === "saleup") {
      products.sort((a: any, b: any) => {
        if (
          (a.warehouseCount <= 0 && b.warehouseCount <= 0) ||
          (a.warehouseCount > 0 && b.warehouseCount > 0)
        ) {
          if (a.saleActive === b.saleActive) {
            let priceA = a.saleActive ? a.salePrice : a.price;
            let priceB = b.saleActive ? b.salePrice : b.price;
            // price asc
            return priceA < priceB ? -1 : 1;
          }
          // sale first
          return a.saleActive ? -1 : 1;
        } else {
          // stocked first
          return a.warehouseCount <= 0 ? 1 : -1;
        }
      });
    }
    return products;
  }

  componentDidUpdate(
    prevProps: Readonly<ProductsProps>,
    prevState: Readonly<ProductsState>
  ) {
    if (this.state.filtersOpen !== prevState.filtersOpen) {
      document.body.style.overflow = this.state.filtersOpen ? "hidden" : "auto";
    }
    
    const locationChanged =
      (this.props.locationSearch || "") !== (prevProps.locationSearch || "");

    // Re-apply search/filter whenever URL query changes
    if (locationChanged) {
      const currentSearchValue = this.hasSearchValue() || "";
      this.setState(
        {
          searchValue: currentSearchValue,
          currentPage: 1,
          visibleCount: this.getInitialVisibleCount(),
        },
        () => {
          this.applyFilter();
        }
      );
    }

    if (
      this.props.infiniteScroll &&
      (this.state.filteredProducts.length !== prevState.filteredProducts.length ||
        this.state.visibleCount !== prevState.visibleCount)
    ) {
      this.setupInfiniteScrollObserver();
    }
  }

  clearFilters = () => {
    this.filteredBrands.clear();
    this.filteredUsages.clear();
    this.filteredTypes.clear();

    this.setState(
      {
        sale: false,
        notSale: false,
        club: false,
        searchValue: "",
      },
      () => this.applyFilter()
    );
  };

  getTypeCount = (type: string) => {
    return this.props.products.filter((p) => p.types.includes(type)).length;
  };
  getUsageCount = (usage: string) => {
    return this.props.products.filter((p) => p.usages.includes(usage)).length;
  };
  // getBrandCount = (brandName: string) => {
  //   return this.props.products.filter((p) => p.brandName === brandName).length;
  // };

  getSaleCount = () => {
    return this.props.products.filter((p) => p.saleActive).length;
  };
  getNotSaleCount = () => {
    return this.props.products.filter((p) => !p.saleActive).length;
  };
  getClubCount = () => {
    return this.props.products.filter((p: any) => p.clubPrice != null && Number(p.clubPrice) > 0).length;
  };

  render() {
    return (
      <section
        className={
          "products-container bordered" +
          (this.state.filtersOpen ? "disabled" : "")
        }
        id="products-content"
      >
        {this.state.filtersOpen && (
          <div
            className="passive"
            onClick={() => this.setState({ filtersOpen: false })}
          />
        )}
        <div
          className={
            "products-filter" + (this.state.filtersOpen ? " filter-open" : "")
          }
        >
          <div className="filter-section-title">
            <span>Филтер</span>
            <span
              className="filter-close"
              onClick={() => this.setState({ filtersOpen: false })}
            >
              <IoClose size={30} />
            </span>
          </div>
          <ProductsFilter
            sale={this.state.sale}
            notSale={this.state.notSale}
            club={this.state.club}
            showClubFilter={!!this.props.client?.company?.clubMember}
            brands={this.state.brandSet}
            usageSet={this.state.usageSet}
            typeSet={this.state.typeSet}
            onChange={(e) => this.onFilterChange(e)}
            getTypeCount={this.getTypeCount}
            getUsageCount={this.getUsageCount}
            // getBrandCount={this.getBrandCount}
            getSaleCount={this.getSaleCount}
            getNotSaleCount={this.getNotSaleCount}
            getClubCount={this.getClubCount}
          />
          <div className="filter-open-bottom">
            <div
              className="filter-open-button"
              onClick={() => this.setState({ filtersOpen: false })}
            >
              {this.state.filteredProducts.length} илэрц
            </div>
            <div className="clear-button" onClick={this.clearFilters}>
              <LiaBroomSolid size={30} color="grey" />
            </div>
          </div>
        </div>
        <div className="products-content">
          <div className="products-title">
            <div className="products-count">
              Нийт
              <span> | {this.state.filteredProducts.length} бүтээгдэхүүн</span>
            </div>
            <div
              className="filter-button"
              onClick={() => this.setState({ filtersOpen: true })}
            >
              Филтер
            </div>
            <div className="filter-dropdown">
              <Dropdown
                icon="chevron down"
                options={this.state.sortOptions}
                defaultValue={this.state.sortValue}
                onChange={this.onSortChange}
              />
            </div>
          </div>
          <div className="products-body">
            {this.getPaginatedProducts() &&
            this.getPaginatedProducts().length > 0 ? (
              this.getPaginatedProducts().map((product: any) => {
                return (
                  <Col
                    className="product-item-col"
                    xs={6}
                    sm={4}
                    md={4}
                    lg={3}
                    key={`product-${product.id}`}
                  >
                    <ProductItem product={product} />
                  </Col>
                );
              })
            ) : (
              <div className="no-product">Бүтээгдэхүүн олдсонгүй</div>
            )}
          </div>
          
          {this.state.filteredProducts && this.state.filteredProducts.length > 0 && (
            <>
              {this.props.infiniteScroll ? (
                <>
                  {this.hasMoreToLoad() ? (
                    <div
                      ref={this.loadMoreSentinelRef}
                      className="infinite-scroll-sentinel flex-div justify-content-center align-items-center margin-top-20"
                    >
                      <button
                        type="button"
                        className="infinite-scroll-load-more"
                        onClick={this.loadMore}
                      >
                        Дараагийн хуудас
                      </button>
                    </div>
                  ) : (
                    <div ref={this.loadMoreSentinelRef} className="infinite-scroll-end margin-top-20" />
                  )}
                  <div className="infinite-scroll-status margin-top-20 text-center">
                    Харуулж буй: {Math.min(this.state.visibleCount, this.state.filteredProducts.length)} / {this.state.filteredProducts.length}
                  </div>
                  {/* <div className="page-size-selector flex-div justify-content-center align-items-center margin-top-20">
                    <div className='flex-div align-items-center' style={{ marginRight: '10px' }}>Нэг удаагийн ачаалах:</div>
                    <Dropdown
                      selection
                      options={this.pageSizeOptions.filter((o) => o.value !== 'all')}
                      value={this.state.pageSize === 'all' ? 50 : this.state.pageSize}
                      onChange={this.onPageSizeChange}
                      placeholder="Тоо"
                      compact
                    />
                  </div> */}
                </>
              ) : (
                <>
              <div className="flex-div margin-top-20 justify-content-center pagination-wrapper">
                <Pagination
                  activePage={this.state.currentPage}
                  totalPages={this.getTotalPages()}
                  onPageChange={this.onPageChange}
                  firstItem={null}
                  lastItem={null}
                />
              </div>
              
              <div className="page-size-selector flex-div justify-content-center align-items-center margin-top-20">
                <div className='flex-div align-items-center' style={{ marginRight: '10px' }}>ХУУДАСНЫ ХЭМЖЭЭ:</div>
                <Dropdown
                  selection
                  options={this.pageSizeOptions}
                  value={this.state.pageSize}
                  onChange={this.onPageSizeChange}
                  placeholder="Хуудасны хэмжээ"
                  compact
                />
              </div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    client: state.client,
    
  };
};

export default connect(mapStateToProps)(Products);
